/**
 * Shared chat normalizer and deterministic conversation key generator for Web.
 */

export const normalizeDepartmentKey = (dept) => {
  if (!dept) return "RECEPTION";
  const cleaned = String(dept).trim().toUpperCase().replace(/[- ]/g, "_");
  if (cleaned.includes("RECEPT") || cleaned.includes("FRONT")) return "RECEPTION";
  if (cleaned.includes("HOUSE") || cleaned.includes("CLEAN") || cleaned.includes("LAUNDRY")) return "HOUSEKEEPING";
  if (cleaned.includes("MAINT") || cleaned.includes("REPAIR") || cleaned.includes("FIX")) return "MAINTENANCE";
  if (cleaned.includes("DIN") || cleaned.includes("FOOD") || cleaned.includes("KITCH") || cleaned.includes("REST")) return "ROOM_DINING";
  return cleaned;
};

export const getConversationKey = (item) => {
  if (!item) return "";

  // 1. Check for Guest Session
  const guestSessionId = item.guestSessionId || item.GuestSessionId || (item.senderType === "Guest" ? item.senderGuestSessionId : null);
  if (guestSessionId) {
    return `guest:${String(guestSessionId).toLowerCase()}`;
  }

  // 2. Check for Staff-to-Staff Direct Message
  const participant1 = item.participant1UserId || item.Participant1UserId || item.senderUserId || item.SenderUserId;
  const participant2 = item.participant2UserId || item.Participant2UserId || item.recipientUserId || item.RecipientUserId;
  const isDirect = item.type === "StaffToStaff" || item.Type === "StaffToStaff" || (participant1 && participant2 && String(participant1).toLowerCase() !== String(participant2).toLowerCase());

  if (isDirect && participant1 && participant2) {
    const sorted = [String(participant1).toLowerCase(), String(participant2).toLowerCase()].sort();
    return `staff:${sorted[0]}_${sorted[1]}`;
  }

  // 3. Check for Department Channel
  const department = item.targetDepartment || item.TargetDepartment || item.department || item.Department;
  if (department) {
    return `dept:${normalizeDepartmentKey(department)}`;
  }

  // 4. Fallback to Conversation ID
  const id = item.conversationId || item.ConversationId || item.id || item.Id;
  return id ? `conv:${String(id).toLowerCase()}` : "";
};

export const normalizeChatMessage = (raw) => {
  if (!raw) return null;

  const id = raw.id || raw.Id || `msg-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
  const conversationId = raw.conversationId || raw.ConversationId || "";
  const senderType = raw.senderType || raw.SenderType || "Staff";
  const senderDisplayName = raw.senderDisplayName || raw.SenderDisplayName || "Staff";
  const senderUserId = raw.senderUserId || raw.SenderUserId || null;
  const senderGuestSessionId = raw.senderGuestSessionId || raw.SenderGuestSessionId || null;
  const targetType = raw.targetType || raw.TargetType || "Department";
  const targetDepartment = raw.targetDepartment || raw.TargetDepartment || null;
  const recipientUserId = raw.recipientUserId || raw.RecipientUserId || null;
  const guestSessionId = raw.guestSessionId || raw.GuestSessionId || null;
  const roomName = raw.roomName || raw.RoomName || null;
  const content = raw.content ?? raw.Content ?? "";
  const attachmentUrl = raw.attachmentUrl || raw.AttachmentUrl || null;
  const attachmentType = raw.attachmentType || raw.AttachmentType || null;
  const metadataJson = raw.metadataJson || raw.MetadataJson || null;
  const isRead = Boolean(raw.isRead ?? raw.IsRead ?? false);
  const createdAt = raw.createdAt || raw.CreatedAt || new Date().toISOString();

  const convKey = getConversationKey({
    conversationId,
    guestSessionId,
    senderGuestSessionId,
    senderType,
    senderUserId,
    recipientUserId,
    targetDepartment,
    targetType,
  });

  return {
    id,
    conversationId,
    senderType,
    senderDisplayName,
    senderUserId,
    senderGuestSessionId,
    targetType,
    targetDepartment,
    recipientUserId,
    guestSessionId,
    roomName,
    content,
    attachmentUrl,
    attachmentType,
    metadataJson,
    isRead,
    createdAt,
    key: convKey,
  };
};

export const normalizeChatConversation = (raw) => {
  if (!raw) return null;

  const id = raw.id || raw.Id || null;
  const type = raw.type || raw.Type || "Department";
  const guestSessionId = raw.guestSessionId || raw.GuestSessionId || null;
  const roomName = raw.roomName || raw.RoomName || null;
  const guestName = raw.guestName || raw.GuestName || null;
  const department = raw.department || raw.Department || raw.targetDepartment || raw.TargetDepartment || null;
  const targetDepartment = department;
  const participant1UserId = raw.participant1UserId || raw.Participant1UserId || null;
  const participant2UserId = raw.participant2UserId || raw.Participant2UserId || null;
  const otherParticipantName =
    raw.otherParticipantName ||
    raw.OtherParticipantName ||
    raw.otherParticipantDisplayName ||
    raw.participantName ||
    null;

  const title =
    raw.title ||
    raw.Title ||
    (type === "StaffToStaff"
      ? otherParticipantName || "Colleague"
      : roomName
      ? `Room ${roomName}${guestName ? ` (${guestName})` : ""}`
      : guestName || department || "Chat");

  const lastMessageSnippet = raw.lastMessageSnippet || raw.LastMessageSnippet || raw.lastMessageContent || "";
  const lastMessageAt = raw.lastMessageAt || raw.LastMessageAt || raw.lastMessageAtUtc || raw.LastMessageAtUtc || null;
  const lastMessageAtUtc = lastMessageAt;
  const unreadCount = Number(raw.unreadCount ?? raw.UnreadCount ?? raw.staffUnreadCount ?? raw.StaffUnreadCount ?? 0);
  const staffUnreadCount = Number(raw.staffUnreadCount ?? raw.StaffUnreadCount ?? unreadCount);
  const isActive = Boolean(raw.isActive ?? raw.IsActive ?? true);

  const convKey = getConversationKey({
    id,
    type,
    guestSessionId,
    targetDepartment,
    participant1UserId,
    participant2UserId,
  });

  return {
    id,
    type,
    guestSessionId,
    roomName,
    guestName,
    department,
    targetDepartment,
    participant1UserId,
    participant2UserId,
    otherParticipantName: otherParticipantName || (type === "StaffToStaff" ? title : null),
    title,
    lastMessageSnippet,
    lastMessageAt,
    lastMessageAtUtc,
    unreadCount,
    staffUnreadCount,
    isActive,
    key: convKey,
  };
};

export const deduplicateMessages = (list) => {
  if (!Array.isArray(list)) return [];
  const seenIds = new Set();
  const result = [];

  for (const msg of list) {
    if (!msg) continue;
    const norm = normalizeChatMessage(msg);
    if (!norm) continue;

    if (seenIds.has(norm.id)) continue;
    seenIds.add(norm.id);
    result.push(norm);
  }

  return result.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
};
